// ============================================================================
// ui/quote-attach.js — the quote tab's optional "anything else" attachments
// ============================================================================
// Ryan 2026-08-09: *"leave it a bit more generic… if they want to upload a logo
// or an additional logo or a picture of a weight they already have that they
// want us to match or whatever the case is, we just need to make it clear that
// they CAN upload things there but they don't HAVE to, because the work has
// already been done when they uploaded the logo originally."*
//
// ── WHY THIS IS NOT A LOGO UPLOAD, AND MUST NEVER BECOME ONE ─────────────────
// The tool is gated on a logo: nobody reaches this tab without having supplied
// one, and `uploadArtwork` already ships both the original file and the approved
// print-ready art with every quote. A second "upload your logo" here would ask a
// buyer to do a job they finished at step 2 — which is exactly why Ryan deleted
// the last one (2026-08-05: *"get rid of the logo upload"*). So the copy leads
// with what we ALREADY have, and everything here is spare capacity: a vector
// version, a photo of the plates they want matched, a PO.
//   ⚠ If you ever find yourself writing "required" or a submit gate into this
// module, the feature has turned back into the thing that was removed.
//
// ── THE CEILINGS ARE THE WORKER'S, NOT NEW ONES ──────────────────────────────
// 10MB/file is `MAX_ARTWORK_BYTES` in `weight-designer-artwork.js`, which is
// itself the existing `/upload` ceiling. Five files is a client-side cap chosen
// so a buyer cannot queue a 50MB serial upload behind a submit button; the
// worker takes them ONE PER REQUEST (its `formData()` buffers whole bodies), so
// the cost of a generous cap here is paid in wall-clock at the worst moment.
// Rejections are shown inline and never block submit — see `ui/product-step.js`,
// where a failed attachment upload is non-fatal by the same rule the artwork is.
// ============================================================================

import { h, replaceChildren } from './dom.js';
import { isPhone } from './mobile-launch.js';

const MAX_FILES = 5;
const MAX_BYTES = 10 * 1024 * 1024;

/* Mirrors the worker's ALLOWED set. `image/*` is deliberately in the `accept`
   attribute even though the extension list repeats part of it: on iOS it is what
   makes the photo library a first-class option in the picker, and "a picture of
   the plates we already have" is the attachment this feature exists for. */
const ACCEPT = '.pdf,.ai,.eps,.png,.jpg,.jpeg,.webp,.svg,.psd,.gif,.heic,.heif,image/*,application/pdf';

const ICON = '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
  + '<path d="M21.44 11.05 12.25 20.24a5 5 0 0 1-7.07-7.07l9.19-9.19a3.5 3.5 0 0 1 4.95 4.95l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>';

function sizeText(bytes) {
  return bytes < 1024 * 1024
    ? `${Math.round(bytes / 1024)} KB`
    : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * @returns {{el: HTMLElement, files: () => File[], clear: () => void}}
 */
export function createQuoteAttach() {
  /** @type {File[]} */
  let picked = [];

  const input = h('input', {
    type: 'file', class: 'wd-attach__input', multiple: true, accept: ACCEPT, tabindex: '-1',
    'aria-hidden': 'true',
  });
  const listEl = h('div', { class: 'wd-attach__list' });
  const errEl = h('div', { class: 'wd-attach__error', hidden: true, role: 'status' });

  /* ⚠ "Drag and drop" is a lie on a phone, and this tab is used on phones more
     than anywhere else. One string per device rather than one string that is
     half wrong on both — the same reason the corpus is going device-aware. */
  const zone = h('div', {
    class: 'wd-attach__zone', tabindex: '0', role: 'button',
    'aria-label': 'Add files to your quote request',
  },
  h('span', { class: 'wd-attach__icon', 'aria-hidden': 'true', html: ICON }),
  h('p', { class: 'wd-attach__prompt' }, isPhone() ? 'Tap to add files' : 'Drag and drop files here, or browse'),
  /* ⚠ HEIC/HEIF ARE NAMED HERE BECAUSE THEY ARE ACCEPTED (Ryan 2026-08-09: *"if you
     added HEIC and HEIF, include that on the list"*). An accepted format missing from
     the list is worse than an unlisted one being rejected: an iPhone owner reads the
     list, does not see their format, and goes and converts a file we would have taken. */
  h('p', { class: 'wd-attach__hint' }, `PDF, AI, EPS, PNG, JPG, SVG, PSD, HEIC or HEIF, up to ${MAX_BYTES / (1024 * 1024)} MB each`));

  const el = h('div', { class: 'wd-attach' },
    zone, input, listEl, errEl,
    /* THE REASSURANCE IS THE POINT OF THE FEATURE (see the header). It sits BELOW
       the zone rather than above it so the control reads first and the caveat
       second: someone who has nothing to add should be able to skip the whole
       block on sight. */
    h('p', { class: 'wd-attach__note' },
      'We already have the logo from your design, so nothing is needed here. Add anything else that helps us quote like a vector version of your logo or any photos of existing weights you’re wanting to match.'));

  function showError(msg) {
    errEl.textContent = msg;
    errEl.hidden = false;
  }
  function clearError() {
    errEl.hidden = true;
    errEl.textContent = '';
  }

  function render() {
    replaceChildren(listEl, picked.map((f, i) =>
      h('div', { class: 'wd-attach__row' },
        h('span', { class: 'wd-attach__name' }, f.name),
        h('span', { class: 'wd-attach__size' }, sizeText(f.size)),
        h('button', {
          type: 'button', class: 'wd-attach__remove', 'aria-label': `Remove ${f.name}`,
          onClick: () => { picked.splice(i, 1); clearError(); render(); },
        }, h('span', {
          'aria-hidden': 'true',
          html: '<svg viewBox="0 0 14 14" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="3" y1="3" x2="11" y2="11"/><line x1="11" y1="3" x2="3" y2="11"/></svg>',
        })))));
    zone.classList.toggle('is-full', picked.length >= MAX_FILES);
  }

  function add(fileList) {
    clearError();
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    const rejected = [];
    for (const f of incoming) {
      if (picked.length >= MAX_FILES) { rejected.push(`${MAX_FILES} files is the limit`); break; }
      if (f.size > MAX_BYTES) { rejected.push(`${f.name} is over ${MAX_BYTES / (1024 * 1024)} MB`); continue; }
      // Same name AND same size: a genuine re-pick of one file, not two files that
      // happen to share a name. Cheap dedupe for the "tapped browse twice" case.
      if (picked.some((p) => p.name === f.name && p.size === f.size)) continue;
      picked.push(f);
    }
    if (rejected.length) showError(rejected.join('. ') + '. Email anything larger to sales@blueskyfitsupply.com.');
    render();
  }

  zone.addEventListener('click', () => input.click());
  zone.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); input.click(); }
  });
  input.addEventListener('change', () => {
    add(input.files);
    input.value = '';        // so re-picking the same file fires `change` again
  });
  zone.addEventListener('dragover', (e) => { e.preventDefault(); zone.classList.add('is-dragover'); });
  zone.addEventListener('dragleave', () => zone.classList.remove('is-dragover'));
  zone.addEventListener('drop', (e) => {
    e.preventDefault();
    zone.classList.remove('is-dragover');
    add(e.dataTransfer && e.dataTransfer.files);
  });

  return {
    el,
    files: () => picked.slice(),
    clear() { picked = []; clearError(); render(); },
  };
}
